/**
 * School-time mode — parent-controlled per-profile schedule.
 *
 * Auto-activates Mon–Fri between profile.school_start and profile.school_end.
 * `school_mode_override`:
 *   `auto`   → follow the schedule (default)
 *   `home`   → force-OFF (snow day / weekend / vacation)
 *   `school` → force-ON  (Saturday field trip / summer school)
 *
 * Tile visibility:
 *   time_scope="anytime"      → always visible
 *   time_scope="home_only"    → hidden when isSchoolMode === true
 *   time_scope="school_only"  → visible ONLY when isSchoolMode === true
 */

function parseHHMM(s) {
  if (!s || typeof s !== "string") return null;
  const m = s.match(/^([01]\d|2[0-3]):([0-5]\d)$/);
  if (!m) return null;
  return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
}

export function isSchoolMode(profile, now = new Date()) {
  if (!profile) return false;
  const override = profile.school_mode_override || "auto";
  if (override === "home") return false;
  if (override === "school") return true;
  // Auto: Mon–Fri only.
  const day = now.getDay(); // 0=Sun … 6=Sat
  if (day === 0 || day === 6) return false;
  const start = parseHHMM(profile.school_start) ?? 8 * 60;
  const end = parseHHMM(profile.school_end) ?? 15 * 60;
  const cur = now.getHours() * 60 + now.getMinutes();
  return cur >= start && cur < end;
}

/** Filter a tile list by the profile's current school-mode state. */
export function filterTilesBySchoolMode(tiles, profile, now = new Date()) {
  const schoolOn = isSchoolMode(profile, now);
  return tiles.filter((t) => {
    const scope = t.time_scope || "anytime";
    if (scope === "anytime") return true;
    if (scope === "home_only") return !schoolOn;
    if (scope === "school_only") return schoolOn;
    return true;
  });
}

/** Same heartbeat trick as dayNight.js — round to next 5 min so school
 * transitions (8:00, 3:00) are picked up promptly without busy-looping. */
export function msUntilNextSchoolCheck(now = new Date()) {
  const t = new Date(now);
  t.setSeconds(0, 0);
  t.setMinutes(Math.floor(t.getMinutes() / 5) * 5 + 5);
  return Math.max(15_000, t.getTime() - now.getTime());
}
